'use strict';

/**
 * Cursor anchor utilities for JSON text editing.
 *
 * The cursor in a JSON textarea is tracked as a logical anchor (path within the
 * JSON tree + offset inside the token at that path) instead of a raw character
 * offset. This makes the cursor stable across reformatting (indentation, key
 * reordering, whitespace normalisation, etc).
 */

export type JsonPath = ( string | number )[];

export type CursorAnchor = {
    path : JsonPath;

    /**
     * Signed character offset from the anchor token's start position.
     *  - 0..tokenLength : cursor inside or at end of token
     *  - negative       : cursor sits N chars before the token starts (leading whitespace)
     *  - > tokenLength  : cursor sits N chars beyond the token end (trailing whitespace)
     */
    offset : number;

    /**
     * True if the anchor refers to the property key rather than its value.
     * Only meaningful when the parent of `path` is an object.
     */
    atPropertyKey? : boolean;
};

type Token = {
    path : JsonPath;
    start : number;
    end : number;
    isPropertyKey : boolean;
};

/**
 * Walk the JSON source and yield every token (primitive values and property
 * keys) with its location. Stops silently on parse errors so we can still
 * produce useful anchors for partially-valid JSON during typing.
 */
const tokenize = ( source : string ) : Token[] => {
    const tokens : Token[] = [];
    const path : ( string | number )[] = [];
    let i = 0;

    const skipWhitespace = () => {
        while ( i < source.length && /\s/.test( source[ i ] ) ) i++;
    };

    const readString = () : { start : number; end : number; value : string } | null => {
        if ( source[ i ] !== '"' ) return null;
        const start = i;
        i++;
        let value = '';
        while ( i < source.length && source[ i ] !== '"' ) {
            if ( source[ i ] === '\\' && i + 1 < source.length ) {
                value += source[ i ] + source[ i + 1 ];
                i += 2;
            }
            else {
                value += source[ i ];
                i++;
            }
        }
        if ( i >= source.length ) return { start, end: i, value };
        i++; // closing quote
        return { start, end: i, value };
    };

    const readPrimitive = () : { start : number; end : number } => {
        const start = i;
        while ( i < source.length && !/[\s,}\]]/.test( source[ i ] ) ) i++;
        return { start, end: i };
    };

    const parseValue = () : void => {
        skipWhitespace();
        if ( i >= source.length ) return;
        const ch = source[ i ];

        if ( ch === '{' ) {
            i++;
            skipWhitespace();
            if ( source[ i ] === '}' ) { i++; return; }
            while ( i < source.length ) {
                skipWhitespace();
                // tolerate trailing comma: ",}" after we already consumed the comma below
                if ( source[ i ] === '}' ) { i++; return; }
                const keyTok = readString();
                if ( !keyTok ) return;
                tokens.push( { path: [ ...path, keyTok.value ], start: keyTok.start, end: keyTok.end, isPropertyKey: true } );
                skipWhitespace();
                if ( source[ i ] !== ':' ) return;
                i++;
                path.push( keyTok.value );
                parseValue();
                path.pop();
                skipWhitespace();
                if ( source[ i ] === ',' ) { i++; continue; }
                if ( source[ i ] === '}' ) { i++; return; }
                return;
            }
        }
        else if ( ch === '[' ) {
            i++;
            skipWhitespace();
            if ( source[ i ] === ']' ) { i++; return; }
            let idx = 0;
            while ( i < source.length ) {
                skipWhitespace();
                if ( source[ i ] === ']' ) { i++; return; }
                path.push( idx );
                parseValue();
                path.pop();
                idx++;
                skipWhitespace();
                if ( source[ i ] === ',' ) { i++; continue; }
                if ( source[ i ] === ']' ) { i++; return; }
                return;
            }
        }
        else if ( ch === '"' ) {
            const tok = readString();
            if ( tok ) tokens.push( { path: [ ...path ], start: tok.start, end: tok.end, isPropertyKey: false } );
        }
        else {
            const tok = readPrimitive();
            if ( tok.end > tok.start ) tokens.push( { path: [ ...path ], start: tok.start, end: tok.end, isPropertyKey: false } );
        }
    };

    try {
        parseValue();
    }
    catch {
        // partial parse is fine
    }
    return tokens;
};

/**
 * Compute a logical anchor for a given character offset in the JSON source.
 * Returns null if the source can't be tokenised meaningfully.
 */
export const getAnchor = ( source : string, offset : number ) : CursorAnchor | null => {
    const tokens = tokenize( source );
    if ( !tokens.length ) return null;

    let containing : Token | undefined,
        nextAfter  : Token | undefined,
        lastBefore : Token | undefined;

    for ( const tok of tokens ) {
        if ( offset >= tok.start && offset <= tok.end ) {
            containing = tok;
            break;
        }
        if ( tok.end < offset ) lastBefore = tok;
        else if ( tok.start > offset && !nextAfter ) nextAfter = tok;
    }

    // Choose an anchor token: the containing one if any, otherwise the closer
    // neighbour. Ties favour lastBefore so punctuation/whitespace immediately
    // following a token sticks to that token.
    let anchorToken : Token | undefined;
    if ( containing ) {
        anchorToken = containing;
    }
    else if ( lastBefore && nextAfter ) {
        const distBefore = offset - lastBefore.end,
              distAfter  = nextAfter.start - offset;
        anchorToken = distBefore <= distAfter ? lastBefore : nextAfter;
    }
    else {
        anchorToken = lastBefore ?? nextAfter;
    }

    if ( !anchorToken ) return null;

    return {
        path         : anchorToken.path,
        offset       : offset - anchorToken.start,
        atPropertyKey: anchorToken.isPropertyKey
    };
};

const pathsEqual = ( a : JsonPath, b : JsonPath ) : boolean => {
    if ( a.length !== b.length ) return false;
    for ( let i = 0; i < a.length; i++ ) if ( a[ i ] !== b[ i ] ) return false;
    return true;
};

/**
 * Resolve a logical anchor back to a character offset in a (possibly
 * reformatted) JSON source. Returns null if the anchor cannot be resolved.
 */
export const resolveAnchor = ( source : string, anchor : CursorAnchor ) : number | null => {
    const tokens = tokenize( source );
    if ( !tokens.length ) return null;

    const target = tokens.find( t =>
        pathsEqual( t.path, anchor.path ) && t.isPropertyKey === !!anchor.atPropertyKey
    ) ?? tokens.find( t => pathsEqual( t.path, anchor.path ) );

    if ( !target ) return null;

    const resolved = target.start + anchor.offset;
    return Math.max( 0, Math.min( resolved, source.length ) );
};
